import { create } from 'zustand'
import { persist } from 'zustand/middleware'

export const locales = ['en', 'ko', 'zh'] as const
export type Locale = (typeof locales)[number]

interface LanguageState {
	locale: Locale
	hasBeenSet: boolean // Track if user has explicitly set language
	setLocale: (locale: Locale) => void
}

/**
 * Detect browser language and map to supported locale
 * Falls back to English if not supported
 */
function detectBrowserLocale(): Locale {
	if (typeof window === 'undefined') return 'en'

	// Get browser language (e.g., 'ko', 'ko-KR', 'zh-CN', 'zh-TW', 'en-US')
	const browserLang = navigator.language || (navigator as any).userLanguage || 'en'
	const langCode = browserLang.split('-')[0].toLowerCase()

	// Map to supported locales
	if (langCode === 'ko') return 'ko'
	if (langCode === 'zh') return 'zh'

	// Default to English
	return 'en'
}

export const useLanguageStore = create<LanguageState>()(
	persist(
		set => ({
			locale: 'en',
			hasBeenSet: false,
			setLocale: locale => set({ locale, hasBeenSet: true }),
		}),
		{
			name: 'onautopilot-language',
			onRehydrateStorage: () => (state, error) => {
				if (error) {
					console.error('Error rehydrating language store:', error)
					return
				}

				// If user hasn't explicitly set a language, detect from browser
				if (state && !state.hasBeenSet) {
					const detectedLocale = detectBrowserLocale()
					// Don't mark as "set" - just update the locale for auto-detection
					// This way, if they visit again with a different browser language,
					// it will auto-detect again until they manually choose
					state.locale = detectedLocale
				}
			},
		}
	)
)
